import React, { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Plus, Trash2, GlobeIcon, KeyIcon, SettingsIcon, FileDownIcon, X, Info, EyeIcon, EyeOffIcon, Braces, Wand2 } from "lucide-react";
import { Textarea } from "../ui/textarea";
import SchemaPropertyEditor from "./SchemaPropertyEditor";
import APIImportModal from "./APIImportModal";
import { Tooltip } from "../ui/tooltip";
import { APIActionConfig, Parameter } from "../../types/AgentAction";
import type { SchemaProperty } from "./SchemaPropertyEditor";
import { isSensitiveHeader, useSensitiveHeaderVisibility } from "../../utils/headerSecurity";

interface AgentActionAPIEditProps {
  config: APIActionConfig;
  onChange: (config: APIActionConfig) => void;
  actionName?: string;
  description?: string;
  onActionNameChange?: (name: string) => void;
  onDescriptionChange?: (description: string) => void;
  showSaveActions?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
  isValid?: boolean;
}

const AgentActionAPIEdit: React.FC<AgentActionAPIEditProps> = ({ 
  config, 
  onChange,
  actionName,
  description,
  onActionNameChange,
  onDescriptionChange,
  showSaveActions,
  onSave,
  onCancel,
  isSaving,
  isValid
}) => {
  const [formData, setFormData] = useState<APIActionConfig>({
    endpoint: "",
    method: "POST",
    headers: { "Content-Type": "application/json" },
    timeout: 30,
    parameters: [],
    requestSchema: {},
    responseSchemas: {},
    base_url: "",
    auth: {
      type: "none",
      token: "",
      header: "Authorization",
      key: "",
    },
    ...config
  });

  // Function to convert our UI format to OpenAPI JSON when saving
  const convertToOpenAPIFormat = (config: APIActionConfig): any => {
    const endpoint = config.endpoint || "/api";
    let path: string;
    let serverUrl: string;
    
    // Handle both full URLs and relative paths
    if (endpoint.startsWith("http://") || endpoint.startsWith("https://")) {
      // Full URL provided
      try {
        const parsedUrl = new URL(endpoint);
        path = parsedUrl.pathname || "/api";
        serverUrl = parsedUrl.origin;
      } catch (e) {
        // Fallback for invalid URLs
        path = "/api";
        serverUrl = "https://example.com";
      }
    } else {
      // Relative path provided (e.g., "/search/providers" or "search/providers")
      path = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
      serverUrl = null; // No server URL for relative paths - will use base_url at runtime
    }
    
    const method = (config.method || "POST").toLowerCase();
    
    const openAPISpec: any = {
      openapi: "3.0.0",
      info: {
        title: "API Configuration",
        version: "1.0.0"
      },
      servers: serverUrl ? [
        {
          url: serverUrl
        }
      ] : [],
      paths: {
        [path]: {
          [method]: {
            operationId: "apiCall",
            description: "API call configuration",
            parameters: [],
            responses: {}
          }
        }
      },
      components: {
        securitySchemes: {}
      },
      response_type: (config as any)?.response_type || "real"
    };

    // Add base_url if specified
    if (config.base_url) {
      openAPISpec.base_url = config.base_url;
    }

    const operation = openAPISpec.paths[path][method];

    // Add path and query parameters
    if (config.parameters && Array.isArray(config.parameters)) {
      config.parameters.forEach(param => {
        operation.parameters.push({
          name: param.name,
          in: param.in,
          schema: { 
            type: param.type,
            default: param.defaultValue,
            enum: param.enum
          },
          required: param.required || false,
          description: param.description
        });
      });
    }

    // Add headers as parameters
    if (config.headers) {
      Object.entries(config.headers).forEach(([name, value]) => {
        if (name !== "Content-Type") { // Content-Type is handled in requestBody
          operation.parameters.push({
            name,
            in: "header",
            schema: { type: "string", default: value },
            required: false
          });
        }
      });
    }

    // Add request body schema
    if (config.requestSchema && Object.keys(config.requestSchema).length > 0) {
      const requestSchema = convertSchemaPropertyToOpenAPI(config.requestSchema);
      operation.requestBody = {
        content: {
          "application/json": {
            schema: requestSchema
          }
        }
      };
    }

    // Add response schemas
    const mockResponses: Record<string, any> = {};
    if (config.responseSchemas) {
      Object.entries(config.responseSchemas).forEach(([statusCode, schema]) => {
        const responseSchema = convertSchemaPropertyToOpenAPI(schema);
        operation.responses[statusCode] = {
          description: `Response ${statusCode}`,
          content: {
            "application/json": {
              schema: responseSchema
            }
          }
        } as any;

        const mockText = responseMocks[statusCode];
        if (mockText && mockText.trim() !== "") {
          try {
            const body = JSON.parse(mockText);
            (operation.responses[statusCode] as any)["x-maitai-mock"] = {
              body
            };
            mockResponses[statusCode] = body;
          } catch (_) {
            (operation.responses[statusCode] as any)["x-maitai-mock"] = {
              body_text: mockText
            };
          }
        }
      });
    }
    if (Object.keys(mockResponses).length > 0) {
      (openAPISpec as any).mock_responses = mockResponses;
    }

    // Add authentication
    if (config.auth && config.auth.type !== "none") {
      if (config.auth.type === "bearer") {
        openAPISpec.components.securitySchemes.bearerAuth = {
          type: "http",
          scheme: "bearer"
        };
        operation.security = [{ bearerAuth: [] }];
      } else if (config.auth.type === "api_key") {
        openAPISpec.components.securitySchemes.apiKeyAuth = {
          type: "apiKey",
          name: config.auth.header || "Authorization",
          in: "header"
        };
        operation.security = [{ apiKeyAuth: [] }];
      }
    }

    return openAPISpec;
  };

  // Helper function to convert SchemaProperty format to OpenAPI schema format
  const convertSchemaPropertyToOpenAPI = (properties: { [key: string]: SchemaProperty }): any => {
    const openAPISchema: any = {
      type: "object",
      properties: {},
      required: []
    };

    Object.entries(properties).forEach(([propName, prop]) => {
      const schemaProp: any = {
        type: prop.type,
      };

      if (prop.description) schemaProp.description = prop.description;
      if (prop.format) schemaProp.format = prop.format;
      if (prop.enum) schemaProp.enum = prop.enum;

      if (prop.type === "object" && prop.properties) {
        const nested = convertSchemaPropertyToOpenAPI(prop.properties);
        schemaProp.properties = nested.properties;
        if (nested.required.length > 0) schemaProp.required = nested.required;
      } else if (prop.type === "array" && prop.items) {
        schemaProp.items = convertSchemaPropertyToOpenAPI({ item: prop.items }).properties.item;
      }

      openAPISchema.properties[propName] = schemaProp;
      if (prop.required) openAPISchema.required.push(propName);
    });

    return openAPISchema;
  };

  const [isOpenAPIImportModalOpen, setIsOpenAPIImportModalOpen] = useState(false);
  const [isAddResponseSchemaModalOpen, setIsAddResponseSchemaModalOpen] = useState(false);
  const [newStatusCode, setNewStatusCode] = useState("");
  const [responseMocks, setResponseMocks] = useState<Record<string, string>>({});
  
  // Use the centralized hook for managing sensitive header visibility
  const { visibleHeaders, toggleHeaderVisibility, isHeaderVisible } = useSensitiveHeaderVisibility();



  // Initialize mock state from config when component loads
  useEffect(() => {
    if (config && typeof config === "object") {
      // Initialize mock responses from config
      const configMockResponses = (config as any)?.mock_responses;
      if (configMockResponses && typeof configMockResponses === "object") {
        const textMap: Record<string, string> = {};
        Object.entries(configMockResponses).forEach(([code, body]) => {
          try {
            textMap[code] = JSON.stringify(body, null, 2);
          } catch (_) {
            // fall back to string if not serializable
            textMap[code] = String(body);
          }
        });
        setResponseMocks(textMap);
      }
    }
  }, [config]);

  useEffect(() => {
    // Convert to OpenAPI format when saving
    const openAPIConfig = convertToOpenAPIFormat(formData);
    // Preserve response_type and implied_base_url from parent config to avoid overriding
    const mergedConfig = {
      ...openAPIConfig,
      response_type: (config as any)?.response_type || openAPIConfig.response_type
    };
    // Preserve implied_base_url if it exists in config (this is read-only information from agent hierarchy)
    if ((config as any)?.implied_base_url) {
      (mergedConfig as any).implied_base_url = (config as any).implied_base_url;
    }
    onChange(mergedConfig);
  }, [formData, responseMocks, config, onChange]);

  const handleInputChange = (field: keyof APIActionConfig, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleHeaderChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      headers: {
        ...prev.headers,
        [key]: value
      }
    }));
  };

  const handleAuthChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      auth: {
        ...prev.auth,
        [field]: value
      }
    }));
  };

  const addHeader = () => {
    const key = `header_${Object.keys(formData.headers || {}).length + 1}`;
    handleHeaderChange(key, "");
  };

  const removeHeader = (key: string) => {
    const newHeaders = { ...formData.headers };
    delete newHeaders[key];
    setFormData(prev => ({
      ...prev,
      headers: newHeaders
    }));
  };

  const handleRequestSchemaChange = (properties: { [key: string]: SchemaProperty }) => {
    handleInputChange("requestSchema", properties);
  };

  const handleResponseSchemaChange = (statusCode: string, properties: { [key: string]: SchemaProperty }) => {
    setFormData(prev => ({
      ...prev,
      responseSchemas: {
        ...prev.responseSchemas,
        [statusCode]: properties
      }
    }));
    // Seed or merge mock with null-valued template as schema evolves
    setResponseMocks(prev => {
      try {
        const template = generateNullTemplateFromSchema(properties);
        const currentText = prev[statusCode];
        if (!currentText || currentText.trim() === "") {
          return { ...prev, [statusCode]: JSON.stringify(template, null, 2) };
        }
        const currentObj = JSON.parse(currentText);
        const merged = deepMergeMissing(currentObj, template);
        return { ...prev, [statusCode]: JSON.stringify(merged, null, 2) };
      } catch (_) {
        return prev;
      }
    });
  };

  const addResponseSchema = (statusCode: string) => {
    setFormData(prev => ({
      ...prev,
      responseSchemas: {
        ...prev.responseSchemas,
        [statusCode]: {}
      }
    }));
    setResponseMocks(prev => ({ ...prev, [statusCode]: "" }));
  };

  const removeResponseSchema = (statusCode: string) => {
    setFormData(prev => {
      const newSchemas = { ...prev.responseSchemas };
      delete newSchemas[statusCode];
      return {
        ...prev,
        responseSchemas: newSchemas
      };
    });
    setResponseMocks(prev => {
      const next = { ...prev } as Record<string, string>;
      delete next[statusCode];
      return next;
    });
  };

  const handleMockChange = (statusCode: string, value: string) => {
    setResponseMocks(prev => ({ ...prev, [statusCode]: value }));
  };

  const formatMockJson = (statusCode: string) => {
    const raw = responseMocks[statusCode] || "";
    try {
      const parsed = raw ? JSON.parse(raw) : {};
      setResponseMocks(prev => ({ ...prev, [statusCode]: JSON.stringify(parsed, null, 2) }));
    } catch (_) {
      // ignore formatting if invalid
    }
  };

  const isValidJson = (text: string | undefined) => {
    if (!text || text.trim() === "") return true;
    try {
      JSON.parse(text);
      return true;
    } catch (_) {
      return false;
    }
  };

  const generateExampleFromSchema = (schema: { [key: string]: SchemaProperty }): any => {
    const result: any = {};
    Object.entries(schema || {}).forEach(([key, prop]) => {
      if (!prop) return;
      switch (prop.type) {
        case "string":
          result[key] = prop.enum && prop.enum.length > 0 ? prop.enum[0] : (prop.format === "date-time" ? new Date().toISOString() : "example");
          break;
        case "number":
        case "integer":
          result[key] = 1;
          break;
        case "boolean":
          result[key] = true;
          break;
        case "array":
          if (prop.items) {
            const itemObj = generateExampleFromSchema({ item: prop.items });
            result[key] = [itemObj.item];
          } else {
            result[key] = [];
          }
          break;
        case "object":
          result[key] = generateExampleFromSchema(prop.properties || {});
          break;
        default:
          result[key] = null;
      }
    });
    return result;
  };

  const generateNullTemplateFromSchema = (schema: { [key: string]: SchemaProperty }): any => {
    const result: any = {};
    
    // Ensure schema is an object and not a string or other type
    if (!schema || typeof schema !== "object" || Array.isArray(schema)) {
      return result;
    }
    
    Object.entries(schema).forEach(([key, prop]) => {
      // Ensure prop is an object with a type property
      if (!prop || typeof prop !== "object" || !prop.type) return;
      
      switch (prop.type) {
        case "object":
          result[key] = generateNullTemplateFromSchema(prop.properties || {});
          break;
        case "array":
          result[key] = [];
          break;
        default:
          result[key] = null;
      }
    });
    return result;
  };

  const deepMergeMissing = (target: any, template: any): any => {
    if (Array.isArray(target) || Array.isArray(template)) {
      // Do not attempt to merge arrays; preserve user's array as-is or default
      return target;
    }
    if (typeof target !== "object" || target === null || typeof template !== "object" || template === null) {
      return target;
    }
    const result: any = { ...target };
    Object.keys(template).forEach((key) => {
      if (!(key in result)) {
        result[key] = template[key];
      } else {
        result[key] = deepMergeMissing(result[key], template[key]);
      }
    });
    return result;
  };

  const handleParameterChange = (index: number, field: keyof Parameter, value: any) => {
    setFormData(prev => ({
      ...prev,
      parameters: prev.parameters?.map((param, i) => 
        i === index ? { ...param, [field]: value } : param
      ) || []
    }));
  };

  const addParameter = () => {
    const newParam: Parameter = {
      name: "new_parameter",
      in: "query",
      type: "string",
      required: false,
      description: "",
    };
    setFormData(prev => ({
      ...prev,
      parameters: [...(prev.parameters || []), newParam]
    }));
  };

  const removeParameter = (index: number) => {
    setFormData(prev => ({
      ...prev,
      parameters: prev.parameters?.filter((_, i) => i !== index) || []
    }));
  };

  const handleOpenAPIImport = (importedConfig: APIActionConfig, importedActionName?: string, importedDescription?: string) => {
    // Update formData with imported configuration
    setFormData(prev => ({
      ...prev,
      ...importedConfig
    }));
    // Hydrate mock editor state from imported mock_responses if present
    const importedMocks = (importedConfig as any)?.mock_responses as Record<string, any> | undefined;
    if (importedMocks && typeof importedMocks === "object") {
      const textMap: Record<string, string> = {};
      Object.entries(importedMocks).forEach(([code, body]) => {
        try {
          textMap[code] = JSON.stringify(body, null, 2);
        } catch (_) {
          // fall back to string if not serializable
          textMap[code] = String(body);
        }
      });
      setResponseMocks(prev => ({ ...prev, ...textMap }));
    }
    // Note: response_type is now managed by the parent AgentActionEdit component
    
    // Populate action name and description if they are currently blank
    if (importedActionName && (!actionName || actionName.trim() === "")) {
      onActionNameChange?.(importedActionName);
    }
    
    if (importedDescription && (!description || description.trim() === "")) {
      onDescriptionChange?.(importedDescription);
    }
    
    setIsOpenAPIImportModalOpen(false);
  };

  return (
    <div className="space-y-8">
      {/* Header with title and import button */}
      <div className="flex items-center justify-between py-4">
        <div className="flex items-center gap-2">
          <GlobeIcon className="w-5 h-5 text-maitai-green" />
          <h3 className="text-lg font-gc-med text-foreground">API Configuration</h3>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setIsOpenAPIImportModalOpen(true)}
          className="gap-2"
        >
          <FileDownIcon className="w-4 h-4" />
          Import
        </Button>
      </div>

      {/* Basic Configuration */}
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* HTTP Method */}
          <div className="space-y-3">
            <Label className="text-sm font-gc-med text-foreground">
              HTTP Method <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.method}
              onValueChange={(value) => handleInputChange("method", value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="GET">GET</SelectItem>
                <SelectItem value="POST">POST</SelectItem>
                <SelectItem value="PUT">PUT</SelectItem>
                <SelectItem value="PATCH">PATCH</SelectItem>
                <SelectItem value="DELETE">DELETE</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Timeout */}
          <div className="space-y-3">
            <Label className="text-sm font-gc-med text-foreground">Timeout (seconds)</Label>
            <Input
              type="number"
              min={1}
              max={300}
              value={formData.timeout || 30}
              onChange={(e) => handleInputChange("timeout", parseInt(e.target.value) || 30)}
              placeholder="30"
            />
          </div>
        </div>

        {/* Endpoint URL */}
        <div className="space-y-3">
          <Label className="text-sm font-gc-med text-foreground">
            Endpoint URL <span className="text-red-500">*</span>
          </Label>
          <Input
            value={formData.endpoint || ""}
            onChange={(e) => handleInputChange("endpoint", e.target.value)}
            placeholder="https://api.example.com/endpoint"
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Use {"{{ parameter_name }}"} for dynamic values in the URL path or query parameters.
          </p>
          {/* Show resolved URL if we have implied_base_url */}
          {(config as any)?.implied_base_url && formData.endpoint && !formData.endpoint.startsWith("http://") && !formData.endpoint.startsWith("https://") && (
            <div className="pl-4 border-l-2 border-muted">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Resolved URL:</span>
                <span className="font-mono text-sm break-all text-maitai-green">
                  {(config as any).implied_base_url.replace(/\/$/, "")}/{formData.endpoint.replace(/^\//, "")}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Base URL Override */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-gc-med text-foreground">Base URL Override</Label>
            <Tooltip content="Override the agent's default base URL for this specific action. If set, this base URL will be used instead of the agent's configured base URL. Leave empty to use the agent's default base URL or full URLs.">
              <Info className="w-4 h-4 text-muted-foreground cursor-help" />
            </Tooltip>
          </div>
          <Input
            type="url"
            value={formData.base_url || ""}
            onChange={(e) => handleInputChange("base_url", e.target.value)}
            placeholder="https://api.override.com"
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Optional: Override the agent's default base URL for this action only.
          </p>
          {/* Show inherited base URL if implied_base_url exists and no override is set */}
          {(config as any)?.implied_base_url && !formData.base_url && (
            <div className="text-xs text-muted-foreground">
              Inherited base URL: <span className="font-mono">{(config as any).implied_base_url}</span>
            </div>
          )}
        </div>

        {/* Parameters Section */}
        <div className="space-y-4 border-t border-border pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-maitai-green" />
              <h3 className="text-lg font-gc-med text-foreground">
                Parameters <span className="text-muted-foreground text-sm font-normal">({formData.parameters?.length || 0})</span>
              </h3>
              <Tooltip content="Path and query parameters that can be used as dynamic values in your API calls. Path parameters are part of the URL path, query parameters are added as URL query strings.">
                <Info className="w-4 h-4 text-muted-foreground cursor-help" />
              </Tooltip>
            </div>
            <Button
              onClick={addParameter}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Parameter
            </Button>
          </div>
          
          {formData.parameters && formData.parameters.length > 0 ? (
            <div className="space-y-3">
              {formData.parameters.map((param, index) => (
                <div key={index} className="border border-border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Input
                        value={param.name}
                        onChange={(e) => handleParameterChange(index, "name", e.target.value)}
                        placeholder="Parameter name"
                        className="font-mono text-sm w-40"
                      />
                      <Select
                        value={param.in}
                        onValueChange={(value: "path" | "query") => handleParameterChange(index, "in", value)}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="path">Path</SelectItem>
                          <SelectItem value="query">Query</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select
                        value={param.type}
                        onValueChange={(value) => handleParameterChange(index, "type", value)}
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="string">String</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="boolean">Boolean</SelectItem>
                          <SelectItem value="integer">Integer</SelectItem>
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={param.required || false}
                          onChange={(e) => handleParameterChange(index, "required", e.target.checked)}
                          className="rounded"
                        />
                        Required
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-muted-foreground">
                        {"{{ " + param.name + " }}"}
                      </div>
                      <Button
                        onClick={() => removeParameter(index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Input
                      value={param.description || ""}
                      onChange={(e) => handleParameterChange(index, "description", e.target.value)}
                      placeholder="Parameter description (optional)"
                      className="text-sm"
                    />
                    {param.defaultValue !== undefined && (
                      <div className="text-xs text-muted-foreground">
                        Default: <span className="font-mono">{JSON.stringify(param.defaultValue)}</span>
                      </div>
                    )}
                    {param.enum && param.enum.length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        Allowed values: <span className="font-mono">{param.enum.join(", ")}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No parameters defined.</p>
              <p className="text-xs mt-1">Click "Add Parameter" to define path or query parameters, or import from an OpenAPI specification.</p>
            </div>
          )}
        </div>
      </div>

      {/* Request Schema */}
      {(formData.method === "POST" || formData.method === "PUT" || formData.method === "PATCH") && (
        <div className="space-y-4 border-t border-border pt-6">
          <div className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-maitai-green" />
            <h3 className="text-lg font-gc-med text-foreground">
              Request Schema <span className="text-red-500">*</span>
            </h3>
            <Tooltip content="Define the structure of data that will be sent in the request body. Each property can reference dynamic values using {{ parameter_name }} syntax.">
              <Info className="w-4 h-4 text-muted-foreground cursor-help" />
            </Tooltip>
          </div>
          <SchemaPropertyEditor
            properties={formData.requestSchema || {}}
            onChange={handleRequestSchemaChange}
          />
        </div>
      )}

      {/* Response Schemas */}
      <div className="space-y-4 border-t border-border pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-maitai-green" />
            <h3 className="text-lg font-gc-med text-foreground">
              Response Schemas <span className="text-muted-foreground text-sm font-normal">(Optional)</span>
            </h3>
            <Tooltip content="Define the expected structure of API responses for different status codes (200, 400, 500, etc.).">
              <Info className="w-4 h-4 text-muted-foreground cursor-help" />
            </Tooltip>
          </div>
          <Button
            onClick={() => setIsAddResponseSchemaModalOpen(true)}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Add New Schema
          </Button>
        </div>
        
        {/* Response Schema List */}
        <div className="space-y-4">
          {Object.entries(formData.responseSchemas || {}).map(([statusCode, schema]) => (
            <div key={statusCode} className="border border-border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-mono ${
                    statusCode.startsWith("2") ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300" :
                    statusCode.startsWith("4") ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300" :
                    statusCode.startsWith("5") ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300" :
                    "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300"
                  }`}>
                    {statusCode}
                  </span>
                  <span className="text-sm font-gc-med text-foreground">
                    Response Schema
                  </span>
                </div>
                <Button
                  onClick={() => removeResponseSchema(statusCode)}
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <SchemaPropertyEditor
                properties={schema}
                onChange={(properties) => handleResponseSchemaChange(statusCode, properties)}
              />
              {/* Mock Response Editor */}
              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Braces className="w-4 h-4 text-maitai-green" />
                    <span className="text-sm font-gc-med text-foreground">Mock Response</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => formatMockJson(statusCode)}
                      disabled={!responseMocks[statusCode] || !isValidJson(responseMocks[statusCode])}
                      className="gap-1"
                      title="Format JSON"
                    >
                      <Wand2 className="w-4 h-4" /> Format
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const example = generateExampleFromSchema(schema);
                        setResponseMocks(prev => ({ ...prev, [statusCode]: JSON.stringify(example, null, 2) }));
                      }}
                      className="gap-1"
                      title="Auto-fill from schema"
                    >
                      <Braces className="w-4 h-4" /> Auto-fill
                    </Button>
                  </div>
                </div>
                <Textarea
                  rows={6}
                  value={responseMocks[statusCode] || ""}
                  onChange={(e) => handleMockChange(statusCode, e.target.value)}
                  placeholder={'{\n  "id": 123,\n  "name": "Example"\n}'}
                  className={`font-mono text-sm ${isValidJson(responseMocks[statusCode]) ? "" : "border-red-500"}`}
                />
                {!isValidJson(responseMocks[statusCode]) && (
                  <div className="text-xs text-red-500">Invalid JSON</div>
                )}
                <div className="text-xs text-muted-foreground">
                  This mock will be returned for {statusCode} responses when Response Type is set to Mock. Leave empty to exclude this status code from mocking.
                </div>
              </div>
            </div>
          ))}
          
          {Object.keys(formData.responseSchemas || {}).length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No response schemas defined.</p>
              <p className="text-xs mt-1">Click "Add New Schema" to define response structures for different status codes.</p>
            </div>
          )}
        </div>
      </div>

      {/* Headers Configuration */}
      <div className="space-y-4 border-t border-border pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-maitai-green" />
            <h3 className="text-lg font-gc-med text-foreground">Headers</h3>
          </div>
          <Button
            onClick={addHeader}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Header
          </Button>
        </div>
        <div className="space-y-4">
          {Object.entries(formData.headers || {}).map(([key, value]) => (
            <div key={key} className="flex items-center gap-3">
              <div className="flex-1">
                <Input
                  placeholder="Header name"
                  value={key}
                  onChange={(e) => {
                    const newKey = e.target.value;
                    const newHeaders = { ...formData.headers };
                    delete newHeaders[key];
                    newHeaders[newKey] = value;
                    setFormData(prev => ({ ...prev, headers: newHeaders }));
                  }}
                  className="font-mono text-sm"
                />
              </div>
              <div className="flex-1 flex items-center gap-2">
                <Input
                  placeholder="Header value"
                  type={isSensitiveHeader(key) && !isHeaderVisible(key) ? "password" : "text"}
                  value={value}
                  onChange={(e) => handleHeaderChange(key, e.target.value)}
                  className="font-mono text-sm flex-1"
                />
                {isSensitiveHeader(key) && (
                  <Button
                    onClick={() => toggleHeaderVisibility(key)}
                    variant="ghost"
                    size="sm"
                    className="p-1 h-8 w-8"
                    title={isHeaderVisible(key) ? "Hide value" : "Show value"}
                  >
                    {isHeaderVisible(key) ? (
                      <EyeOffIcon className="w-4 h-4" />
                    ) : (
                      <EyeIcon className="w-4 h-4" />
                    )}
                  </Button>
                )}
              </div>
              <Button
                onClick={() => removeHeader(key)}
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          {Object.keys(formData.headers || {}).length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No headers configured. Click "Add Header" to add custom headers.
            </p>
          )}
        </div>
      </div>

      {/* Authentication */}
      <div className="space-y-4 border-t border-border pt-6">
        <div className="flex items-center gap-2">
          <KeyIcon className="w-5 h-5 text-maitai-green" />
          <h3 className="text-lg font-gc-med text-foreground">Authentication</h3>
        </div>
        <div className="space-y-6">
          <div className="space-y-3">
            <Label className="text-sm font-gc-med text-foreground">Authentication Type</Label>
            <Select
              value={formData.auth?.type || "none"}
              onValueChange={(value) => handleAuthChange("type", value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="bearer">Bearer Token</SelectItem>
                <SelectItem value="api_key">API Key</SelectItem>
                <SelectItem value="basic">Basic Auth</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.auth?.type === "bearer" && (
            <div className="space-y-3">
              <Label className="text-sm font-gc-med text-foreground">Bearer Token</Label>
              <Input
                type="password"
                value={formData.auth?.token || ""}
                onChange={(e) => handleAuthChange("token", e.target.value)}
                placeholder="Enter bearer token"
                className="font-mono text-sm"
              />
            </div>
          )}

          {formData.auth?.type === "api_key" && (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">Header Name</Label>
                <Input
                  value={formData.auth?.header || ""}
                  onChange={(e) => handleAuthChange("header", e.target.value)}
                  placeholder="X-API-Key"
                  className="font-mono text-sm"
                />
              </div>
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">API Key</Label>
                <Input
                  type="password"
                  value={formData.auth?.key || ""}
                  onChange={(e) => handleAuthChange("key", e.target.value)}
                  placeholder="Enter API key"
                  className="font-mono text-sm"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Response Schema Modal */}
      {isAddResponseSchemaModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-gc-med text-foreground">Add Response Schema</h3>
              <Button
                onClick={() => {
                  setIsAddResponseSchemaModalOpen(false);
                  setNewStatusCode("");
                }}
                variant="ghost"
                size="sm"
                className="p-1"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-gc-med text-foreground">Status Code</Label>
                <Select
                  value={newStatusCode}
                  onValueChange={setNewStatusCode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a status code" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="200">200 - OK</SelectItem>
                    <SelectItem value="201">201 - Created</SelectItem>
                    <SelectItem value="204">204 - No Content</SelectItem>
                    <SelectItem value="400">400 - Bad Request</SelectItem>
                    <SelectItem value="401">401 - Unauthorized</SelectItem>
                    <SelectItem value="403">403 - Forbidden</SelectItem>
                    <SelectItem value="404">404 - Not Found</SelectItem>
                    <SelectItem value="422">422 - Unprocessable Entity</SelectItem>
                    <SelectItem value="500">500 - Internal Server Error</SelectItem>
                    <SelectItem value="502">502 - Bad Gateway</SelectItem>
                    <SelectItem value="503">503 - Service Unavailable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-gc-med text-foreground">Custom Status Code</Label>
                <Input
                  type="number"
                  value={newStatusCode}
                  onChange={(e) => setNewStatusCode(e.target.value)}
                  placeholder="Enter custom status code (e.g., 299)"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  You can also enter a custom status code if it's not in the list above.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddResponseSchemaModalOpen(false);
                  setNewStatusCode("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (newStatusCode && !formData.responseSchemas?.[newStatusCode]) {
                    addResponseSchema(newStatusCode);
                    setIsAddResponseSchemaModalOpen(false);
                    setNewStatusCode("");
                  }
                }}
                disabled={!newStatusCode || !!formData.responseSchemas?.[newStatusCode]}
              >
                Add Schema
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* OpenAPI Import Modal */}
      <APIImportModal
        isOpen={isOpenAPIImportModalOpen}
        onClose={() => setIsOpenAPIImportModalOpen(false)}
        onImport={handleOpenAPIImport}
      />

      {/* Save/Cancel Actions */}
      {showSaveActions && (
        <div className="flex justify-end gap-3 pt-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={isSaving || !isValid}
            className="bg-maitai-green hover:bg-maitai-green/90 text-white"
          >
            {isSaving ? "Saving..." : "Save Action"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AgentActionAPIEdit; 